import { Container } from 'inversify';
import widgetsContainer from '@mobx/containers/widgets';
import serviceContainer from '@mobx/containers/service';
import entitiesContainer from '@mobx/containers/entities';
import featuresContainer from '@mobx/containers/features';
import { type AuthService, AuthServiceIdentifier } from '@shared/types/mobx/services/auth';
import { type RolesService, RolesServiceIdentifier } from '@shared/types/mobx/services/roles';
import { type OsiService, OsiServiceIdentifier } from '@shared/types/mobx/services/osi';
import { type AccountReportsModule, AccountReportsModuleIdentifier } from '@shared/types/mobx/services/accountReports';
import { ProfileModule } from '@mobx/services/profile';
import { RegistrationModule } from '@mobx/services/registration';
import { AppartmentsModule } from '@mobx/services/appartments';
import pagesContainer from './containers/pages';

const rootContainer = Container.merge(
  serviceContainer,
  pagesContainer,
  widgetsContainer,
  featuresContainer,
  entitiesContainer
);
// DI

export const authModule = rootContainer.get<AuthService>(AuthServiceIdentifier);

/**
 * Оркестратор очистки СЕССИОННЫХ сторов.
 *
 * ПОЧЕМУ ЗДЕСЬ, А НЕ В AuthModule: AuthModule уже инжектится в RolesModule/ProfileModule/OsiService
 * и др. Прямой инжект этих модулей обратно в AuthModule дал бы циклическую DI. root.ts же резолвит
 * все синглтоны ПОСТ-фактум из уже собранного контейнера (не через конструкторную инъекцию), поэтому
 * имеет доступ ко всем модулям без цикла. Интерцепторы (reactQuery/config/notary/utils) и так
 * импортируют authModule из этого файла и используют его лениво (в рантайме коллбэка), поэтому
 * могут импортировать отсюда же clearSession-оркестратор — цикла не возникает, т.к. @mobx/root
 * никуда не инжектится.
 */
const rolesModule = rootContainer.get<RolesService>(RolesServiceIdentifier);
const osiService = rootContainer.get<OsiService>(OsiServiceIdentifier);
const accountReportsModule = rootContainer.get<AccountReportsModule>(AccountReportsModuleIdentifier);
const profileModule = rootContainer.get<ProfileModule>(ProfileModule);
const registrationModule = rootContainer.get<RegistrationModule>(RegistrationModule);
const appartmentsModule = rootContainer.get<AppartmentsModule>(AppartmentsModule);

/**
 * Очищает persist ВСЕХ сессионных сторов + сбрасывает их поля к initial.
 * Иначе roles/currentRole/userData протекают между сессиями и роль «застревает».
 * Несессионные сторы (settings/contact/checkContact) НЕ трогаем.
 */
export const clearAllSessions = async (): Promise<void> => {
  await Promise.all([
    authModule.clearSession(),
    rolesModule.clearSession(),
    profileModule.clearSession(),
    registrationModule.clearSession(),
    appartmentsModule.clearSession(),
    osiService.clearSession(),
    accountReportsModule.clearSession()
  ]);
};

/**
 * Штатный выход (кнопка). Сохраняем существующую логику authModule.logout()
 * (обнуление token/id/userData + редирект goToHome) и добавляем очистку persist сессии.
 */
export const logout = async (): Promise<void> => {
  authModule.logout();
  await clearAllSessions();
};

/**
 * Протухшая сессия (401 из интерцептора). Сохраняем существующую логику
 * authModule.sessionExpired() (token, редирект goToCheckContact) и добавляем очистку persist сессии.
 */
export const sessionExpired = async (): Promise<void> => {
  authModule.sessionExpired();
  await clearAllSessions();
};

export { rootContainer };
